import { useState, useCallback, useEffect } from 'react';
import { ProfileFormData, AvatarType, NotificationType, FormErrors, ProfileState } from '../types';

export const useProfileForm = () => {
	const [formData, setFormData] = useState<ProfileFormData>({
		nameValue: '',
		lastNameValue: '',
		countryCode: '',
		mobileNumber: '',
		country: '',
		companyValue: '',
		occupationValue: '',
	});

	const [avatar, setAvatar] = useState<AvatarType>('men');
	const [notification, setNotification] = useState<NotificationType>('daily');
	const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);
	const [errors, setErrors] = useState<FormErrors>({});

	const updateFormField = useCallback(
		(field: keyof ProfileFormData, value: string) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
			// Clear error when field is updated
			if (errors[field]) {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				});
			}
		},
		[errors],
	);

	const validateStep1 = useCallback((): boolean => {
		const newErrors: FormErrors = {};

		if (!formData.nameValue.trim()) {
			newErrors.nameValue = 'Name is required';
		}
		if (!formData.lastNameValue.trim()) {
			newErrors.lastNameValue = 'Last name is required';
		}
		if (!formData.mobileNumber.trim()) {
			newErrors.mobileNumber = 'Mobile number is required';
		}
		if (!formData.country) {
			newErrors.country = 'Country is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [formData]);

	const getProfileState = useCallback((): ProfileState => {
		return {
			formData,
			avatar,
			notification,
			notificationsEnabled,
		};
	}, [formData, avatar, notification, notificationsEnabled]);

	// Load defaults from localStorage (previously saved personal information)
	useEffect(() => {
		try {
			const saved = localStorage.getItem('personalInformation');
			if (saved) {
				const parsed = JSON.parse(saved) as {
					name?: string;
					surname?: string;
					countryCode?: string;
					mobileNumber?: string;
					country?: string;
					companyName?: string;
					companyPosition?: string;
					avatar?: AvatarType;
					notifications?: NotificationType;
					notificationsEnabled?: boolean;
				};

				setFormData((prev) => ({
					nameValue: parsed.name ?? prev.nameValue,
					lastNameValue: parsed.surname ?? prev.lastNameValue,
					countryCode: parsed.countryCode ?? prev.countryCode,
					mobileNumber: parsed.mobileNumber ?? prev.mobileNumber,
					country: parsed.country ?? prev.country,
					companyValue: parsed.companyName ?? prev.companyValue,
					occupationValue: parsed.companyPosition ?? prev.occupationValue,
				}));

				if (parsed.avatar) setAvatar(parsed.avatar);
				if (parsed.notifications) setNotification(parsed.notifications);
				if (typeof parsed.notificationsEnabled === 'boolean') setNotificationsEnabled(parsed.notificationsEnabled);
			}
		} catch {
			// ignore malformed localStorage content
		}
	}, []);

	return {
		formData,
		avatar,
		notification,
		notificationsEnabled,
		errors,
		updateFormField,
		setAvatar,
		setNotification,
		setNotificationsEnabled,
		validateStep1,
		getProfileState,
	};
};
